import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { ChatSquare, CheckCircle, Clock, PencilSquare, Send } from 'react-bootstrap-icons';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDateTime } from '../utils/dateUtils';

const AdminQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    question: '',
    answer: '',
    published: false,
    status: 'pending'
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.api.url}admin/questions`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
        setStats(data.statistics || {});
      } else {
        throw new Error('Failed to fetch questions');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuestion = (question) => {
    setSelectedQuestion(question);
    setFormData({
      title: question.title || '',
      question: question.question || '',
      answer: question.answer || '',
      published: question.published || false,
      status: question.status || 'pending'
    });
    setShowAnswerModal(true);
  };

  const handleUpdateQuestion = async () => {
    if (!selectedQuestion) return;

    try {
      setUpdating(true);
      const response = await fetch(`${config.api.url}admin/questions/${selectedQuestion.questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          question: formData.question,
          answer: formData.answer,
          published: formData.published,
          status: formData.answer.trim() ? 'answered' : 'pending'
        }),
      });

      if (response.ok) {
        setShowAnswerModal(false);
        setSelectedQuestion(null);
        await fetchQuestions(); // Refresh the list
        setError(null); // Clear any previous errors
      } else {
        let errorMessage = `Failed to update question (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
          if (errorData.details) {
            console.error('Server error details:', errorData.details);
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          const errorText = await response.text();
          console.error('Raw error response:', errorText);
          errorMessage += `: ${errorText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating question:', err);
      setError(`Error updating question: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (question) => {
    if (question.answer && question.answer.trim()) {
      if (question.published) {
        return <Badge bg="success"><CheckCircle className="me-1" />Published</Badge>;
      } else {
        return <Badge bg="info"><CheckCircle className="me-1" />Answered</Badge>;
      }
    }
    return <Badge bg="warning"><Clock className="me-1" />Pending</Badge>;
  };

  if (loading) {
    return <LoadingSpinner message="Loading questions..." />;
  }

  return (
    <Container className="my-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>
                <ChatSquare className="me-2" />
                Question Management
              </h2>
              <p className="text-muted mb-0">
                Manage user questions and provide answers
              </p>
            </div>
            <Button variant="outline-primary" onClick={fetchQuestions}>
              Refresh
            </Button>
          </div>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Statistics Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-primary">{stats.total || 0}</h4>
                  <small className="text-muted">Total Questions</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-warning">{stats.pending || 0}</h4>
                  <small className="text-muted">Pending</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-info">{stats.answered || 0}</h4>
                  <small className="text-muted">Answered</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h4 className="text-success">{stats.published || 0}</h4>
                  <small className="text-muted">Published</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Questions Table */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <ChatSquare className="me-2" />
                All Questions
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {questions.length === 0 ? (
                <div className="text-center py-5">
                  <ChatSquare size={48} className="text-muted mb-3" />
                  <h4>No Questions</h4>
                  <p className="text-muted">No questions have been submitted yet.</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Question</th>
                      <th>Asked By</th>
                      <th>Track</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((question) => (
                      <tr key={question.questionId}>
                        <td>
                          <div>
                            <strong>{question.title}</strong>
                            <div className="small text-muted text-truncate" style={{maxWidth: '300px'}}>
                              {question.question}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            {question.username || 'Unknown'}
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {question.track || 'General'}
                          </Badge>
                        </td>
                        <td>
                          <div className="small">
                            {formatDateTime(question.timestamp)}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(question)}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleAnswerQuestion(question)}
                          >
                            <PencilSquare className="me-1" size={14} />
                            {question.answer && question.answer.trim() ? 'Edit' : 'Answer'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Answer/Edit Question Modal */}
          <Modal show={showAnswerModal} onHide={() => setShowAnswerModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                <PencilSquare className="me-2" />
                {selectedQuestion?.answer && selectedQuestion.answer.trim() ? 'Edit Question & Answer' : 'Answer Question'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedQuestion && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Question Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Enter question title"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Question Details</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.question}
                      onChange={(e) => setFormData({...formData, question: e.target.value})}
                      placeholder="Enter question details"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Answer</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={formData.answer}
                      onChange={(e) => setFormData({...formData, answer: e.target.value})}
                      placeholder="Enter your answer here..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="published-checkbox"
                      label="Publish this Q&A (will appear on /tracks/answers)"
                      checked={formData.published}
                      onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    />
                    <Form.Text className="text-muted">
                      Published questions will be visible to all users on the answers page.
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="info">
                    <strong>Email Notifications:</strong>
                    <ul className="mb-0 mt-2">
                      <li>The user who asked this question will be notified when you provide an answer</li>
                      <li>Admin notification email will also receive a copy</li>
                    </ul>
                  </Alert>
                </Form>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAnswerModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpdateQuestion}
                disabled={updating || !formData.answer.trim()}
              >
                {updating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Send className="me-1" size={14} />
                    {selectedQuestion?.answer && selectedQuestion.answer.trim() ? 'Update' : 'Send Answer'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminQuestionsPage;
